// Send history for one invitation, with the message that actually went out.
//
// Bodies are rendered in a sandboxed iframe, never dangerouslySetInnerHTML: this is
// operator-authored HTML being replayed, and it must not run in the app's origin.
// The stored body already has the access password masked (see the API's email.js).
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { sfqApi } from "../../../api/feasibility";
import {
  Backdrop, Modal, Header, Title, CloseBtn, Body, Loading,
} from "./emailModalUI";
import { normalizeAssetUrls } from "./emailTemplates";

const LogItem = styled.div`
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f5;
  &:last-child { border-bottom: none; }
`;

const LogHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LogDate = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #495057;
`;

const LogSender = styled.span`
  font-size: 11px;
  color: #adb5bd;
`;

const LangBadge = styled.span`
  padding: 1px 6px;
  border-radius: 8px;
  background: #e7f1ff;
  color: #0d6efd;
  font-size: 10px;
  font-weight: 600;
`;

// 알림 메일 표시. LangBadge와 같은 모양이되 색을 달리해, 나란히 붙어도 "무슨 메일인가"와
// "무슨 언어인가"가 한눈에 갈라진다.
const TypeBadge = styled.span`
  padding: 1px 6px;
  border-radius: 8px;
  background: #fff3cd;
  color: #997404;
  font-size: 10px;
  font-weight: 600;
`;

const ToggleBtn = styled.button`
  margin-left: auto;
  padding: 2px 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #fff;
  color: #495057;
  font-size: 11px;
  cursor: pointer;
  &:hover { background: #f1f3f5; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const LogRecipients = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-top: 3px;
`;

const Content = styled.div`
  margin-top: 8px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
`;

const Subject = styled.div`
  padding: 7px 10px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-size: 12px;
  font-weight: 600;
  color: #212529;
`;

const BodyFrame = styled.iframe`
  display: block;
  width: 100%;
  height: 320px;
  border: none;
  background: #fff;
`;

const Empty = styled.div`
  padding: 32px 0;
  text-align: center;
  color: #adb5bd;
  font-size: 13px;
`;

const LEGACY_NOTE =
  "이 메일은 발송 내용 기록 기능이 추가되기 전에 보낸 것이라 본문이 남아 있지 않습니다.";

// Same values the send path inlines onto the outgoing mail (the API's email.js) and the
// composer previews, so the history reads back the way the site saw it.
const SHELL = (html) => `<!doctype html>
<html><head><meta charset="utf-8">
<style>
  body { margin:0; padding:14px; font-family:-apple-system,"Segoe UI",Roboto,"Malgun Gothic",sans-serif;
         font-size:14px; line-height:1.6; color:#212529; }
  p { margin:0 0 10px; } p:empty::after { content:"\\00a0"; }
  ul, ol { margin:0 0 10px; padding-left:22px; }
  blockquote { margin:0 0 10px; padding-left:12px; border-left:3px solid #dee2e6; }
  a { color:#0d6efd; word-break:break-all; } hr { border:none; border-top:1px solid #dee2e6; margin:16px 0; }
  img { max-width:100%; height:auto; }
</style></head><body>${html}</body></html>`;

// The logged body keeps embedded images as API URLs (the mail itself carried them as CID
// attachments). Serving those needs the session cookie, which a bare sandbox="" would
// strip along with the frame's origin — scripts stay blocked either way.
const BODY_SANDBOX = "allow-same-origin";

function fmtDateTime(val) {
  if (!val) return "-";
  return new Date(val).toLocaleString("ko-KR", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function EmailLogModal({ sfqId, invId, siteName, onClose }) {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId]   = useState(null);

  useEffect(() => {
    sfqApi.getEmailLogs(sfqId, invId)
      .then((res) => setLogs(res.data.data || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [sfqId, invId]);

  return (
    <Backdrop onClick={(e) => e.target === e.currentTarget && onClose()}>
      <Modal>
        <Header>
          <Title>메일 발송 내역 — {siteName || `Invitation #${invId}`}</Title>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </Header>
        <Body>
          {loading && <Loading>불러오는 중...</Loading>}
          {!loading && logs.length === 0 && <Empty>발송된 메일이 없습니다.</Empty>}

          {!loading && logs.map((log) => {
            // Backfilled rows (migration 023) predate content capture — no body to show.
            const isLegacy = log.mailType === "Legacy" || !log.bodyHtml;
            const isOpen   = openId === log.id;
            // 초대와 알림이 한 목록에 섞여 있다. 둘은 사이트에서 벌어지는 일이 다르므로
            // (초대는 비밀번호를 갈아 끼우고, 알림은 아무것도 바꾸지 않는다) 어느 쪽이
            // 언제 나갔는지가 이 화면을 여는 이유의 절반이다.
            const isReminder = log.mailType === "Reminder";
            // 자동 발송에는 직원이 없어 sentBy 가 센티널 문자열로 들어온다
            // (services/Feasibility/reminder.js 의 SYSTEM_SENDER).
            const isAuto = log.sentBy === "SYSTEM";
            return (
              <LogItem key={log.id}>
                <LogHead>
                  <LogDate>{fmtDateTime(log.sentAt)}</LogDate>
                  <LogSender>
                    {isAuto ? "자동 발송" : `by ${log.sentByName || log.sentBy}`}
                  </LogSender>
                  {isReminder && (
                    <TypeBadge title="답변을 재촉하는 알림 메일입니다. 비밀번호를 재발급하지 않으므로 사이트는 초대 메일의 비밀번호를 계속 씁니다.">
                      알림
                    </TypeBadge>
                  )}
                  {log.lang && <LangBadge>{log.lang === "ko" ? "한국어" : "English"}</LangBadge>}
                  <ToggleBtn
                    onClick={() => setOpenId(isOpen ? null : log.id)}
                    disabled={isLegacy}
                    title={isLegacy ? LEGACY_NOTE : undefined}
                  >
                    {isLegacy ? "내용 없음" : isOpen ? "내용 닫기" : "내용 보기"}
                  </ToggleBtn>
                </LogHead>
                <LogRecipients>To: {log.recipients || "-"}</LogRecipients>

                {isOpen && !isLegacy && (
                  <Content>
                    <Subject>{log.subject}</Subject>
                    <BodyFrame
                      sandbox={BODY_SANDBOX}
                      title={`email-${log.id}`}
                      srcDoc={SHELL(normalizeAssetUrls(log.bodyHtml))}
                    />
                  </Content>
                )}
              </LogItem>
            );
          })}
        </Body>
      </Modal>
    </Backdrop>
  );
}
